import React from "react";
import {
  Form,
  Input,
  Upload,
  Button,
  Select,
  Row,
  Col,
  TimePicker,
  message,
  Alert,
  Space,
  Checkbox,
  Collapse,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import "./RegisterPharmacy.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { Option } = Select;

const RegisterPharmacy = () => {
  const [form] = Form.useForm();
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpVerified, setOtpVerified] = React.useState(false);
  const [otpError, setOtpError] = React.useState("");
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = React.useState(false);
  const [isOpen24Hours, setIsOpen24Hours] = React.useState(false);
  const [pincodeLoading, setPincodeLoading] = React.useState(false);
  const [localities, setLocalities] = React.useState([]);

  const handlePincodeChange = async (e) => {
  const pincode = e.target.value;

  // Reset dependent fields whenever pincode changes
  form.setFieldsValue({
    locality: null,
    cityTown: "",
    district: "",
    state: "",
  });
  setLocalities([]);

  if (pincode.length === 6) {
    setPincodeLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/pincode/${pincode}`);
      const data = await response.json();

      if (data.success) {
        const matchingPincodes = data.data;
        const firstMatch = matchingPincodes[0];

        // Set form values from the first match
        form.setFieldsValue({
          district: firstMatch.district,
          state: firstMatch.statename,
          cityTown: firstMatch.district, // fallback: using district as city/town
        });

        // Build unique localities list
        const uniqueLocalities = [
          ...new Set(matchingPincodes.map((item) => item.officename)),
        ];
        setLocalities(uniqueLocalities);

        // Auto-fill if only one locality
        if (uniqueLocalities.length === 1) {
          form.setFieldsValue({ locality: uniqueLocalities[0] });
        }
      } else {
        message.error("Invalid pincode");
        setLocalities([]);
      }
    } catch (error) {
      console.error("Error fetching pincode data:", error);
      message.error("Failed to fetch pincode data. Please try again.");
      setLocalities([]);
    } finally {
      setPincodeLoading(false);
    }
  }
};
  const handleSendOtp = async () => {
    try {
      const phoneValues = await form.validateFields([
        ["phone", "countryCode"],
        ["phone", "number"],
      ]);
      const phoneNumber = `${phoneValues.phone.number}`;
      setOtpLoading(true);
      setOtpError("");

      const response = await fetch(`${BASE_URL}/otp/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber }),
      });
      const data = await response.json();

      if (!response.ok) {
        // Specifically check for the "already used" error
        if (data.error === "This phone number has already been used.") {
          message.error(data.error);
          setOtpError(data.error);
        } else {
          throw new Error(data.error || "Failed to send OTP");
        }
        return; // Stop execution if there's an error
      }

      message.success(data.message || "OTP sent successfully!");
      setOtpSent(true);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setOtpError(
        error.message ||
          "Failed to send OTP. Please check the number and try again."
      );
      message.error(error.message || "Failed to send OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const phoneValues = await form.getFieldValue(["phone"]);
      const otpValue = form.getFieldValue("otp");
      if (!otpValue || otpValue.length !== 4) {
        setOtpError("Please enter a valid 4-digit OTP.");
        return;
      }
      const phoneNumber = `${phoneValues.number}`;
      setVerifyOtpLoading(true);
      setOtpError("");

      const response = await fetch(`${BASE_URL}/otp/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber, otp: otpValue }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to verify OTP");
      }

      message.success(data.message || "Phone number verified successfully!");
      setOtpVerified(true);
      setOtpSent(false); // Hide OTP input after verification
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setOtpError(error.message || "OTP verification failed.");
      message.error(error.message || "OTP verification failed.");
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (!otpVerified) {
      message.error("Please verify your phone number before submitting.");
      // Scroll to phone number field or show a general message
      const phoneField = document.getElementById("phone_number");
      if (phoneField) {
        phoneField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Focus on the OTP input if it's visible, or the send OTP button
      if (otpSent) {
        const otpInput = document.getElementById("otp");
        if (otpInput) otpInput.focus();
      } else {
        const sendOtpButton = document.getElementById("send-otp-button");
        if (sendOtpButton) sendOtpButton.focus();
      }
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("is_open_24_hours", isOpen24Hours ? "true" : "false");

    if (!isOpen24Hours) {
      const [from, to] = values.openHours || [];
      formData.append("openFrom", from?.format("HH:mm") || "");
      formData.append("openTo", to?.format("HH:mm") || "");
    }
    formData.append("name", values.name);
    formData.append("licenseNumber", values.licenseNumber);

    // Structured address
    formData.append("addressLine1", values.addressLine1);
    formData.append("locality", values.locality);
    formData.append("cityTown", values.cityTown);
    formData.append("district", values.district);
    formData.append("state", values.state);
    formData.append("pincode", values.pincode);

    // Phone with country code
    if (values.phone) {
      formData.append("phoneCountryCode", values.phone.countryCode);
      formData.append("phoneNumber", values.phone.number);
    }

    // WhatsApp with country code
    if (values.whatsapp) {
      formData.append("whatsappCountryCode", values.whatsapp.countryCode);
      formData.append("whatsappNumber", values.whatsapp.number);
    }

    // Google Maps Link (optional)
    if (values.googleMapsLink) {
      formData.append("googleMapsLink", values.googleMapsLink);
    }

    // License Document
    if (values.licenseDoc?.[0]?.originFileObj) {
      formData.append("licenseDoc", values.licenseDoc[0].originFileObj);
    }

    // Pharmacy Images (optional)
    if (values.pharmacyImages && values.pharmacyImages.length > 0) {
      values.pharmacyImages.forEach((file) => {
        if (file.originFileObj) {
          formData.append("pharmacyImages", file.originFileObj);
        }
      });
    }

    formData.append("services", values.services?.join(",") || "");
    formData.append("orderMethods", values.orderMethods?.join(",") || "");
    // openFrom and openTo are now appended conditionally above based on isOpen24Hours

    // For debugging: Log formData entries
    // console.log("FormData entries before sending:");
    // for (let [key, value] of formData.entries()) {
    //   console.log(`${key}: ${value}`);
    // }

    try {
      const response = await fetch(`${BASE_URL}/pharmacy/register`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      console.log("✅ Server response:", data);

      if (response.ok) {
        message.success("Pharmacy registration submitted successfully!");
        form.resetFields();
        setSubmitted(true);
      } else {
        message.error("Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("❌ Submission failed:", err);
      message.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-pharmacy-container">
      <h2>Register Your Pharmacy with BookLocal</h2>

      {submitted ? (
        <Alert
          message="🎉 You're successfully registered with BookLocal!"
          description="Your details have been received and are under verification. We'll notify you once your pharmacy is approved and live on our platform."
          type="success"
          showIcon
          style={{ marginTop: 16 }}
        />
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="register-form"
        >
          <Form.Item
            name="name"
            label="Pharmacy Name"
            rules={[
              { required: true, message: "Please enter the pharmacy name" },
            ]}
          >
            <Input placeholder="Enter pharmacy name" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="licenseNumber"
                label="License Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter the license number",
                  },
                ]}
              >
                <Input placeholder="Enter license number" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="licenseDoc"
                label="Upload License Document"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e) ? e : e && e.fileList
                }
                rules={[
                  {
                    required: true,
                    message: "Please upload the license document",
                  },
                ]}
              >
                <Upload
                  name="licenseDoc"
                  beforeUpload={() => false}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Collapse style={{ marginBottom: 24 }}>
            <Collapse.Panel
              header={
                <span style={{ fontWeight: "bold" }}>Pharmacy Address</span>
              }
              key="1"
            >
              <Form.Item
                name="addressLine1"
                label="Address Line 1"
                rules={[
                  {
                    required: true,
                    message: "Please enter the street address",
                  },
                ]}
              >
                <Input placeholder="E.g., House No, Street Name" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="pincode"
                    label="Pincode"
                    rules={[
                      { required: true, message: "Please enter pincode" },
                      {
                        pattern: /^\d{6}$/,
                        message: "Enter valid 6-digit pincode",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter pincode"
                      onChange={handlePincodeChange}
                      maxLength={6}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="locality"
                    label="Locality / Area"
                    rules={[
                      { required: true, message: "Please enter the locality" },
                    ]}
                  >
                    <Select
                      placeholder="Select locality"
                      loading={pincodeLoading}
                      disabled={localities.length === 0}
                    >
                      {localities.map((locality) => (
                        <Option key={locality} value={locality}>
                          {locality}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="cityTown"
                    label="City / Town"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the city or town",
                      },
                    ]}
                  >
                    <Input placeholder="Enter city or town" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="district"
                    label="District"
                    rules={[
                      { required: true, message: "Please enter the district" },
                    ]}
                  >
                    <Input placeholder="Enter district" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="state"
                    label="State"
                    rules={[
                      { required: true, message: "Please enter the state" },
                    ]}
                  >
                    <Input placeholder="Enter state" />
                  </Form.Item>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>

          <Form.Item
            name="googleMapsLink"
            label="Google Maps Link"
            rules={[
              { required: true, message: "Please enter the Google Maps link" },
              {
                type: "url",
                message:
                  "Please enter a valid Google Maps URL (e.g., https://maps.app.goo.gl/example)",
              },
              {
                pattern:
                  /^(https?:\/\/)?(www\.)?(maps\.app\.goo\.gl\/[a-zA-Z0-9]+|[a-zA-Z0-9.-]+\.google\.com\/maps\b)/,
                message: "Please enter a valid Google Maps URL.",
              },
            ]}
          >
            <Input placeholder="Enter Google Maps link (e.g., https://maps.app.goo.gl/example)" />
          </Form.Item>

          <Form.Item
            name="pharmacyImages"
            label="Upload Pharmacy Images"
            valuePropName="fileList"
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          >
            <Upload
              name="pharmacyImages"
              beforeUpload={() => false}
              listType="picture-card"
              multiple
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Phone Number" required>
                <Input.Group compact>
                  <Form.Item
                    name={["phone", "countryCode"]}
                    noStyle
                    initialValue="+91"
                    rules={[
                      { required: true, message: "Country code is required" },
                    ]}
                  >
                    <Select style={{ width: "25%" }}>
                      <Option value="+91">+91 (India)</Option>
                      {/* Add more country codes as needed */}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name={["phone", "number"]}
                    noStyle
                    rules={[
                      { required: true, message: "Please enter phone number" },
                      {
                        pattern: /^\d{10}$/,
                        message: "Enter valid 10-digit phone number",
                      },
                    ]}
                  >
                    <Input
                      style={{ width: "75%" }}
                      placeholder="Enter phone number"
                      disabled={otpSent || otpVerified}
                      maxLength={10}
                    />
                  </Form.Item>
                </Input.Group>
                {!otpVerified && (
                  <Button
                    id="send-otp-button"
                    onClick={handleSendOtp}
                    loading={otpLoading}
                    disabled={otpSent || otpVerified}
                    style={{ marginTop: 8 }}
                  >
                    {otpSent ? "Resend OTP" : "Send OTP"}
                  </Button>
                )}
                {otpError && (
                  <Alert
                    message={otpError}
                    type="error"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
                {otpVerified && (
                  <Alert
                    message="Phone number verified!"
                    type="success"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
                {!otpVerified &&
                  form.isFieldsTouched(true) &&
                  !form.getFieldError("phone").length && (
                    <Alert
                      message="Please verify your phone number to submit the registration."
                      type="warning"
                      showIcon
                      style={{ marginTop: 10 }}
                    />
                  )}
              </Form.Item>

              {otpSent && !otpVerified && (
                <Form.Item
                  label="Enter OTP"
                  name="otp"
                  rules={[
                    { required: true, message: "Please enter the OTP" },
                    { len: 4, message: "OTP must be 4 digits" },
                  ]}
                  help={otpError}
                  validateStatus={otpError ? "error" : ""}
                  className="otp-input-item"
                >
                  <Space.Compact style={{ width: "100%" }}>
                    <Input
                      placeholder="Enter 4-digit OTP"
                      maxLength={4}
                      className="otp-input"
                    />
                    <Button
                      type="primary"
                      onClick={handleVerifyOtp}
                      loading={verifyOtpLoading}
                    >
                      Verify OTP
                    </Button>
                  </Space.Compact>
                </Form.Item>
              )}
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="WhatsApp Number" required>
                <Input.Group compact>
                  <Form.Item
                    name={["whatsapp", "countryCode"]}
                    noStyle
                    initialValue="+91"
                    rules={[
                      { required: true, message: "Country code is required" },
                    ]}
                  >
                    <Select style={{ width: "25%" }}>
                      <Option value="+91">+91 (India)</Option>
                      {/* Add more country codes as needed */}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name={["whatsapp", "number"]}
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "Please enter WhatsApp number",
                      },
                      {
                        pattern: /^\d{10}$/,
                        message: "Enter valid 10-digit WhatsApp number",
                      },
                    ]}
                  >
                    <Input
                      style={{ width: "75%" }}
                      placeholder="Enter WhatsApp number"
                      maxLength={10}
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="services"
                label="Services Offered"
                rules={[{ required: true, message: "Please select services" }]}
              >
                <Select mode="multiple" placeholder="Select services">
                  <Option value="online_delivery">Online Delivery</Option>
                  <Option value="in_store_pickup">In-Store Pickup</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="orderMethods"
                label="Order Methods"
                rules={[
                  { required: true, message: "Please select order methods" },
                ]}
              >
                <Select mode="multiple" placeholder="Select order methods">
                  <Option value="phone">Phone</Option>
                  <Option value="whatsapp">WhatsApp</Option>
                  <Option value="online">Online</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Checkbox for "Open 24 hours?" */}
          <Form.Item
            name="isOpen24Hours"
            valuePropName="checked" // For Checkbox component
          >
            <Checkbox
              checked={isOpen24Hours}
              onChange={(e) => {
                setIsOpen24Hours(e.target.checked);
                if (e.target.checked) {
                  // If checked, clear and disable operating hours
                  form.setFieldsValue({ openHours: null });
                }
                // Trigger validation for openHours as its requirement changes
                form.validateFields(["openHours"]);
              }}
            >
              <span style={{ fontWeight: "bold" }}>Open 24 hours?</span>
            </Checkbox>
          </Form.Item>

          {/* Operating Hours - conditionally displayed/required */}
          <Form.Item
            name="openHours"
            label="Operating Hours"
            // Required only if not open 24 hours
            rules={[
              {
                required: !isOpen24Hours,
                message: "Please select operating hours",
              },
            ]}
          >
            <TimePicker.RangePicker
              format="h:mm A"
              use12Hours
              minuteStep={30}
              style={{ width: "100%" }}
              disabled={isOpen24Hours} // Disable if "Open 24 hours" is checked
            />
          </Form.Item>

          <Form.Item
            name="agree"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error("You must agree to continue")),
              },
            ]}
          >
            <Checkbox>
              By Continuing, You Agree To Booklocal’s{" "}
              <a
                href="https://www.booklocal.co.in/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms Of Use
              </a>{" "}
              And Acknowledge Our{" "}
              <a
                href="https://www.booklocal.co.in/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              disabled={
                !otpVerified &&
                form.isFieldsTouched(true) &&
                !form.getFieldError("phone").length
              }
            >
              {loading ? "Submitting..." : "Submit Registration"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default RegisterPharmacy;
